import 'Core-utils/helpers/rulesAgGridModules';
import _ from 'lodash';
import { AgGridBase } from 'Components/organisms/AgGridBase';
import { AutoCompleteItem, MenuItem } from 'Core-utils/types/types';
import {
    ADD_COHORT,
    CONFIGURATION_HIGH_RISK_COHORT_LABELS,
    CUSTOM_SCORE_VALUE_MODAL_COHORT,
    DELETE_CUSTOM_SCORE_COHORT,
    HEALTH_SCORE_CONFIGURATION_PLACEHOLDER,
    YES_DELETE_IT,
} from 'Core-utils/constants/constants';
import { RiDeleteBin6Line } from '@react-icons/all-files/ri/RiDeleteBin6Line';
import { DropdownComponent } from 'Components/molecules';
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { generateHealthScoreConfigurationFactors } from 'Core-utils/helpers/helpers';
import { AutoCompleteDropdown } from 'Components/molecules/AutoCompleteDropdown';
import { Button } from 'react-bootstrap';
import { CohortDto, CohortKind, CustomScoreCohortDto, KIND } from '@ampsec/platform-client';
import {
    createHighRiskCohorts,
    deleteHighRiskCohort,
    getCohorts,
    getHighRiskCohorts,
    updateHighRiskCohorts,
} from 'Apis/library';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import { ConfirmationModal } from 'Components/molecules';
import { useI18n } from 'Hooks/useI18n';
import { ToastMessage } from 'Components/molecules';
import { useConnectorData } from 'Hooks/useConnectorsData';
import type { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';
import './styles.scss';

// ─── Types ────────────────────────────────────────────────────────────────────

interface HighRiskCohortGridContext {
    fetchCohorts: CustomScoreCohortDto[];
    healthScoreData: CustomScoreCohortDto[];
    setHealthScoreData: React.Dispatch<React.SetStateAction<CustomScoreCohortDto[]>>;
    rowExpanded: { [key: string]: boolean };
    setRowExpanded: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
    globalDisabledItems: string[];
    connectorsByCategoryGroup: Record<string, any[]> | null;
    setRefreshData: React.Dispatch<React.SetStateAction<boolean>>;
    handleDelete: (row: CustomScoreCohortDto, e: React.MouseEvent<SVGElement, MouseEvent>) => void;
    refresh: () => void;
    persistCohortRow: (row: CustomScoreCohortDto) => Promise<void>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Sentinel SWR cache key meaning "fetch all cohorts (no search filter)".
// Using '' as the key causes getCohorts('') → api.cohorts.list('') which the
// backend interprets as an empty-string filter and returns no results.
const COHORTS_ALL_KEY = '__cohorts_all__';

// Wrapper that converts the sentinel key back to undefined so the API omits
// the query param entirely and returns all cohorts.
const fetchCohortsBySearch = (query: string | null | undefined) =>
    getCohorts(query === COHORTS_ALL_KEY ? undefined : (query || undefined));

const cohortsArray = (items: MenuItem[]) =>
    items.map((item) => ({
        ...item,
        inclusive: item.inclusive ?? true,
        value: item.value ?? '',
        displayValue: item.displayValue ?? '',
        kind: item.kind as CohortKind,
        id: item.id ?? '',
    }));

const isPersistedCohortRow = (rowId: string, persistedCohorts: CustomScoreCohortDto[]) =>
    persistedCohorts.some((cohort) => cohort.id === rowId);

const cohortMemberKey = (cohort: CustomScoreCohortDto) =>
    cohort.cohort
        ?.map((member) => member.id)
        .sort()
        .join('|') ?? '';

const serverHasMatchingCohort = (
    row: CustomScoreCohortDto,
    persistedCohorts: CustomScoreCohortDto[],
) => {
    const rowKey = cohortMemberKey(row);
    if (!rowKey) {
        return false;
    }
    return persistedCohorts.some((persisted) => cohortMemberKey(persisted) === rowKey);
};

const isUnsavedDraftRow = (row: CustomScoreCohortDto, fetchCohorts: CustomScoreCohortDto[]) =>
    !isPersistedCohortRow(row.id, fetchCohorts) &&
    !_.isEmpty(row.cohort) &&
    !serverHasMatchingCohort(row, fetchCohorts);

const mergeCohortsWithDrafts = (
    prev: CustomScoreCohortDto[],
    fetchCohorts: CustomScoreCohortDto[],
): CustomScoreCohortDto[] => {
    const drafts = prev.filter((row) => isUnsavedDraftRow(row, fetchCohorts));
    return [...fetchCohorts, ...drafts];
};

const withoutDraftRow = (rows: CustomScoreCohortDto[], rowId: string): CustomScoreCohortDto[] =>
    rows.filter((item) => item.id !== rowId);

const toggleRefreshFlag = (current: boolean): boolean => !current;

const createRemoveDraftRowUpdater = (rowId: string) => (rows: CustomScoreCohortDto[]) =>
    withoutDraftRow(rows, rowId);

const collapseRowExpandedState = (prev: { [key: string]: boolean }) => {
    const collapsed = { ...prev };
    Object.keys(collapsed).forEach((id) => {
        collapsed[id] = false;
    });
    return collapsed;
};

type PersistCohortHandlers = {
    refresh: () => void;
    removeDraftRow: (rowId: string) => void;
};

async function persistCohortRowToApi(
    row: CustomScoreCohortDto,
    persisted: CustomScoreCohortDto[],
    inFlightRowIds: Set<string>,
    handlers: PersistCohortHandlers,
    options?: { skipRefresh?: boolean },
): Promise<void> {
    if (_.isEmpty(row.cohort) || !row.id) {
        return;
    }

    if (inFlightRowIds.has(row.id) || serverHasMatchingCohort(row, persisted)) {
        return;
    }

    const existingCohort = persisted.find((item) => item.id === row.id);
    if (existingCohort) {
        if (_.isEqual(existingCohort.cohort, row.cohort)) {
            return;
        }
        inFlightRowIds.add(row.id);
        try {
            await updateHighRiskCohorts({
                id: existingCohort.id,
                cohort: row.cohort,
                multiplier: row.multiplier || 1,
            });
            if (!options?.skipRefresh) {
                handlers.refresh();
            }
        } finally {
            inFlightRowIds.delete(row.id);
        }
        return;
    }

    inFlightRowIds.add(row.id);
    try {
        await createHighRiskCohorts({
            cohort: row.cohort,
            multiplier: row.multiplier || 1,
        });
        handlers.removeDraftRow(row.id);
        if (!options?.skipRefresh) {
            handlers.refresh();
        }
    } finally {
        inFlightRowIds.delete(row.id);
    }
}

async function flushPendingCohortRows(
    rows: CustomScoreCohortDto[],
    persistRow: (row: CustomScoreCohortDto, options?: { skipRefresh?: boolean }) => Promise<void>,
): Promise<boolean> {
    const rowsToSave = rows.filter((row) => !_.isEmpty(row.cohort));
    if (rowsToSave.length === 0) {
        return false;
    }
    await Promise.all(rowsToSave.map((row) => persistRow(row, { skipRefresh: true })));
    return true;
}

// ─── Cell renderers ──────────────────────────────────────────────────────────

const CohortCellRenderer = forwardRef((initialProps: ICellRendererParams<CustomScoreCohortDto>, ref) => {
    const [agParams, setAgParams] = useState(initialProps);

    useImperativeHandle(ref, () => ({
        refresh: (newParams: ICellRendererParams<CustomScoreCohortDto>) => {
            setAgParams(newParams);
            return true;
        },
    }));

    const { data, context } = agParams;
    const {
        healthScoreData,
        setHealthScoreData,
        rowExpanded,
        setRowExpanded,
        globalDisabledItems,
        connectorsByCategoryGroup,
        persistCohortRow,
    } = context as HighRiskCohortGridContext;

    const rowId = data?.id ?? '';
    const wasExpandedRef = useRef<boolean | undefined>(undefined);
    const [searchText, setSearchText] = useState<string>('');
    const [filteredData, setFilteredData] = useState<MenuItem[]>([]);

    const { data: fetchCohortsData, isLoading } = useApiObjectData<CohortDto[], MenuItem[]>(
        searchText || COHORTS_ALL_KEY,
        fetchCohortsBySearch,
        (dataItem) =>
            dataItem &&
            dataItem.map((item) => ({
                id: item.value,
                displayValue: item.displayValue,
                value: item.value,
                kind: item.kind,
            })),
    );

    useEffect(() => {
        if (Array.isArray(fetchCohortsData)) {
            setFilteredData(fetchCohortsData);
        }
    }, [fetchCohortsData, searchText, isLoading]);

    // Persist only on expanded → collapsed transitions (not when rowExpanded[id] is
    // merely falsy while another row was collapsed, which caused duplicate creates).
    useEffect(() => {
        const isExpanded = Boolean(rowExpanded[rowId]);
        const wasExpanded = wasExpandedRef.current;
        wasExpandedRef.current = isExpanded;

        if (!wasExpanded || isExpanded) {
            return;
        }

        const healthScoreItem = healthScoreData.find((item) => item.id === rowId);
        if (!healthScoreItem || _.isEmpty(healthScoreItem.cohort)) {
            return;
        }

        void persistCohortRow(healthScoreItem);
        setSearchText('');
    }, [rowExpanded, rowId, healthScoreData, persistCohortRow]);

    const handleAutoCompleteChange = (selectedItems: AutoCompleteItem[]) => {
        const rowData = healthScoreData.find((item) => item.id === data?.id);
        if (rowData?.id) {
            setRowExpanded((prev) => ({ ...prev, [rowData.id]: true }));
        }

        const items = filteredData.filter((item) =>
            selectedItems.some((selectedItem) => selectedItem.id === item.id),
        );

        setHealthScoreData((prevData) => {
            const updatedOptions = [...prevData];
            const existingItemIndex = updatedOptions.findIndex((dataItem) => dataItem.id === rowData?.id);
            if (existingItemIndex !== -1) {
                updatedOptions[existingItemIndex] = {
                    ...updatedOptions[existingItemIndex],
                    cohort: _.uniqBy(
                        [...updatedOptions[existingItemIndex].cohort, ...cohortsArray(items)],
                        'id',
                    ),
                };
            } else {
                updatedOptions.push({
                    ...(rowData as CustomScoreCohortDto),
                    cohort: _.uniqBy([...(rowData?.cohort ?? []), ...cohortsArray(items)], 'id'),
                });
            }
            return updatedOptions;
        });

        setSearchText('');
    };

    const handleChipClose = (closedItem: string) => {
        const isNotClosedItem = (rowData: { id: string }) => rowData.id !== closedItem;
        setHealthScoreData((prevData) =>
            prevData.map((updatedDataItem) => {
                if (updatedDataItem.id === data?.id) {
                    return {
                        ...updatedDataItem,
                        cohort: updatedDataItem.cohort.filter(isNotClosedItem) ?? [],
                    };
                }
                return updatedDataItem;
            }),
        );
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
    };

    const selectedOptions = healthScoreData
        .find((dataItem) => dataItem.id === data?.id)
        ?.cohort.map((cohortData) => ({
            ...cohortData,
            icon: connectorsByCategoryGroup?.['Identity & Access']?.[0]?.imageUrl,
        }));

    return (
        <AutoCompleteDropdown
            dropDownItems={
                filteredData &&
                filteredData.map((cohortData) => ({
                    ...cohortData,
                    icon: connectorsByCategoryGroup?.['Identity & Access']?.[0]?.imageUrl,
                }))
            }
            onSelectedItemsChange={handleAutoCompleteChange}
            disabledItems={globalDisabledItems}
            onCloseChip={handleChipClose}
            selectedItems={selectedOptions ?? []}
            rowIndex={data?.id ?? ''}
            isExpanded={rowExpanded[data?.id ?? '']}
            onChangeSelectedValues={() => {
                setRowExpanded((prev) => ({ ...prev, [data?.id ?? '']: !prev[data?.id ?? ''] }));
            }}
            placeholder={HEALTH_SCORE_CONFIGURATION_PLACEHOLDER[1]}
            onSearchChange={handleSearchChange}
            searchQuery={searchText}
        />
    );
});

const MultiplierCellRenderer = ({ data, context }: ICellRendererParams<CustomScoreCohortDto>) => {
    const { fetchCohorts, healthScoreData, setHealthScoreData, setRefreshData } =
        context as HighRiskCohortGridContext;

    const handleChange = (multiplier: MenuItem) => {
        setHealthScoreData((prevData: any) =>
            prevData.map((item: any) =>
                item.id === data?.id
                    ? { ...item, multiplier: Number(multiplier?.value.slice(0, -2)) }
                    : item,
            ),
        );
        const itemToUpdate = fetchCohorts?.find((dataItem) => dataItem?.id === data?.id);
        if (itemToUpdate?.id) {
            updateHighRiskCohorts({
                id: healthScoreData.find((item) => item.id === data?.id)?.id ?? '',
                cohort:
                    healthScoreData
                        .find((item) => item.id === data?.id)
                        ?.cohort?.map((item) => ({
                            ...item,
                            inclusive: item.inclusive ?? true,
                            value: item.value ?? '',
                            displayValue: item.displayValue ?? '',
                            kind: item.kind as CohortKind,
                            id: item.id ?? '',
                        })) ?? [],
                multiplier: Number(multiplier?.value.slice(0, -2)),
            }).then(() => setRefreshData((prev) => !prev));
        } else {
            createHighRiskCohorts({
                id: data?.id ?? '',
                cohort: [],
                multiplier: Number(multiplier?.value.slice(0, -2)),
            }).then(() => setRefreshData((prev) => !prev));
        }
    };

    return (
        <DropdownComponent
            disableToggle={!fetchCohorts?.find((dataItem) => dataItem?.id === data?.id)?.id}
            value={`${data?.multiplier} ${'x'}` || '1 x'}
            dropDownItems={generateHealthScoreConfigurationFactors()}
            onChange={(value) => handleChange({ id: 0, value: value ?? '' })}
            isStartCase={false}
        />
    );
};

const DeleteCellRenderer = ({ data, context }: ICellRendererParams<CustomScoreCohortDto>) => {
    const { handleDelete } = context as HighRiskCohortGridContext;
    return (
        <RiDeleteBin6Line
            size={20}
            className="settings-page__icon"
            onClick={(e) => data && handleDelete(data, e)}
            role="button"
            aria-label="Delete cohort row"
        />
    );
};

export type HighRiskCohortConfigurationTableHandle = {
    flushPendingCohorts: () => Promise<void>;
};

// ─── Component ────────────────────────────────────────────────────────────────

const HighRiskCohortConfigurationTable = forwardRef<HighRiskCohortConfigurationTableHandle>(function HighRiskCohortConfigurationTable(_props, ref) {
    const { data: fetchCohorts, refresh } = useApiObjectData<CustomScoreCohortDto[], CustomScoreCohortDto[]>(
        KIND.CUSTOM_SCORE_COHORTS,
        getHighRiskCohorts,
        (a) => a,
        { 'sort[multiplier]': 'DESC' },
    );
    const { connectorsByCategoryGroup } = useConnectorData();
    const [idToDelete, setIdToDelete] = useState<string>('');
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    const [healthScoreData, setHealthScoreData] = useState<CustomScoreCohortDto[]>([]);
    const [rowExpanded, setRowExpanded] = useState<{ [key: string]: boolean }>({});
    const [refreshData, setRefreshData] = useState<boolean>(false);
    const gridRef = useRef<AgGridReact<CustomScoreCohortDto> | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const persistInFlightRowIdsRef = useRef<Set<string>>(new Set());
    const healthScoreDataRef = useRef(healthScoreData);
    const { t } = useI18n();

    const safeFetchCohorts = Array.isArray(fetchCohorts) ? fetchCohorts : [];
    const safeFetchCohortsRef = useRef(safeFetchCohorts);

    healthScoreDataRef.current = healthScoreData;
    safeFetchCohortsRef.current = safeFetchCohorts;

    const refreshPersistedCohorts = useCallback(() => {
        setRefreshData(toggleRefreshFlag);
    }, []);

    const removeDraftRow = useCallback((rowId: string) => {
        setHealthScoreData(createRemoveDraftRowUpdater(rowId));
    }, []);

    const persistCohortRow = useCallback(
        (row: CustomScoreCohortDto, options?: { skipRefresh?: boolean }): Promise<void> =>
            persistCohortRowToApi(
                row,
                safeFetchCohortsRef.current,
                persistInFlightRowIdsRef.current,
                { refresh: refreshPersistedCohorts, removeDraftRow },
                options,
            ),
        [refreshPersistedCohorts, removeDraftRow],
    );

    const flushPendingCohorts = useCallback(async (): Promise<void> => {
        const didFlush = await flushPendingCohortRows(healthScoreDataRef.current, persistCohortRow);
        if (!didFlush) {
            return;
        }
        setRefreshData(toggleRefreshFlag);
        setRowExpanded({});
    }, [persistCohortRow]);

    useImperativeHandle(ref, () => ({ flushPendingCohorts }), [flushPendingCohorts]);

    useEffect(
        () => () => {
            void flushPendingCohorts();
        },
        [flushPendingCohorts],
    );

    const extractDisabledItems = (data: CustomScoreCohortDto[]) =>
        Object.values(data).flatMap((item) => item.cohort?.flatMap((v) => v.id) || []);

    const globalDisabledItems = useMemo(
        () => extractDisabledItems(healthScoreData),
        [healthScoreData],
    );

    // Disable only while the last row is an unfinished draft (no cohort chips yet).
    const lastRow = healthScoreData[healthScoreData.length - 1];
    const isButtonDisabled =
        healthScoreData.length > 0 && (lastRow?.cohort?.length ?? 0) === 0;

    const handleRow = () => {
        const genId = Math.floor(10000000 + Math.random() * 90000000)
            .toString(16)
            .slice(2);
        setHealthScoreData((item: any) => [...item, { id: genId, cohort: [], multiplier: 1 }]);
    };

    const handleDelete = (row: CustomScoreCohortDto, e: React.MouseEvent<SVGElement, MouseEvent>) => {
        e.preventDefault();
        setShowConfirmationModal(true);
        setIdToDelete(row?.id);
    };

    const handleDeleteClick = async () => {
        const rowId = idToDelete;
        setShowConfirmationModal(false);
        setIdToDelete('');

        setRowExpanded((prev) => {
            const next = { ...prev };
            delete next[rowId];
            return next;
        });
        setHealthScoreData((prev) => prev.filter((row) => row.id !== rowId));

        if (!isPersistedCohortRow(rowId, safeFetchCohorts)) {
            return;
        }

        try {
            await deleteHighRiskCohort(rowId);
            setRefreshData(toggleRefreshFlag);
        } catch {
            refresh();
        }
    };

    const handleCloseModal = () => {
        setShowConfirmationModal(false);
    };

    useEffect(() => {
        const hasExpandedRow = Object.values(rowExpanded).some(Boolean);
        if (!hasExpandedRow) {
            return undefined;
        }

        const handleOutsideClick = (e: MouseEvent) => {
            const isClickInsideContainer =
                containerRef.current && containerRef.current.contains(e.target as Node);
            if (!isClickInsideContainer) {
                setRowExpanded(collapseRowExpandedState);
            }
        };

        window.addEventListener('click', handleOutsideClick);
        return () => window.removeEventListener('click', handleOutsideClick);
    }, [rowExpanded]);

    useEffect(() => {
        if (!Array.isArray(fetchCohorts)) {
            return;
        }
        setHealthScoreData((prev) => mergeCohortsWithDrafts(prev, fetchCohorts));
        setRowExpanded({});
    }, [fetchCohorts]);

    useEffect(() => {
        refresh();
    }, [refreshData]);

    // Force AG Grid to re-render cells whenever shared state changes
    useEffect(() => {
        gridRef.current?.api?.refreshCells({ force: true });
    }, [rowExpanded, healthScoreData]);

    const columnDefs: ColDef<CustomScoreCohortDto>[] = useMemo(
        () => [
            {
                colId: 'usercohort',
                headerName: CONFIGURATION_HIGH_RISK_COHORT_LABELS[0],
                sortable: false,
                flex: 1,
                autoHeight: true,
                cellRenderer: CohortCellRenderer,
            },
            {
                colId: 'multiplier',
                headerName: CONFIGURATION_HIGH_RISK_COHORT_LABELS[1],
                sortable: false,
                width: 180,
                cellRenderer: MultiplierCellRenderer,
            },
            {
                colId: 'delete',
                headerName: '',
                sortable: false,
                width: 60,
                cellRenderer: DeleteCellRenderer,
            },
        ],
        [],
    );

    const gridContext: HighRiskCohortGridContext = useMemo(
        () => ({
            fetchCohorts: safeFetchCohorts,
            healthScoreData,
            setHealthScoreData,
            rowExpanded,
            setRowExpanded,
            globalDisabledItems,
            connectorsByCategoryGroup,
            setRefreshData,
            handleDelete,
            refresh,
            persistCohortRow,
        }),
        [safeFetchCohorts, healthScoreData, rowExpanded, globalDisabledItems, connectorsByCategoryGroup, persistCohortRow],
    );

    return (
        <div ref={containerRef} className="d-flex flex-column gap-2">
            <div className="d-flex align-items-center">
                <div style={{ width: 'fit-content' }}>
                    <ToastMessage toastSubText={t('CUSTOM_SCORE_HELP')} variant="info" />
                </div>
                <Button
                    variant="body5 border-0 text-text-interactive d-flex align-items-center p-0 ms-auto"
                    onClick={handleRow}
                    disabled={isButtonDisabled}
                >
                    {ADD_COHORT}
                </Button>
            </div>
            <AgGridBase.Root fullWidth>
                <AgGridBase.Table<CustomScoreCohortDto>
                    ref={gridRef}
                    columnDefs={columnDefs}
                    dataMode="client"
                    rowData={healthScoreData}
                    bodyHeight="28rem"
                    movableColumns={false}
                    wrapperClassName="cohort-risk-grid"
                    agGridProps={{
                        context: gridContext,
                        getRowId: (params) => String(params.data?.id ?? ''),
                        suppressContextMenu: true,
                        suppressCellFocus: true,
                        rowSelection: undefined,
                        domLayout: 'normal',
                        rowHeight: 56,
                    }}
                />
            </AgGridBase.Root>
            <ConfirmationModal
                modalTitle={DELETE_CUSTOM_SCORE_COHORT}
                modalBodyTitle={CUSTOM_SCORE_VALUE_MODAL_COHORT}
                onPrimaryButtonClick={handleDeleteClick}
                primaryButtonLabel={YES_DELETE_IT}
                show={showConfirmationModal}
                onClose={handleCloseModal}
                buttonVariant="danger"
            />
        </div>
    );
});

export default HighRiskCohortConfigurationTable;
